import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PanelContextValue {
  openPanel: (id: string) => void;
  closePanel: (id: string) => void;
  openPanelId: string | null;
}

interface PanelProviderProps {
  children: ReactNode;
  history?: number;
}

const PanelContext = createContext<PanelContextValue | null>(null);

/*
 * This context is used to manage the open panels. We dont want panels to ever get stacked so
 * we keep track of panels in a given context and make sure only one is ever open at a time.
 */
export const PanelProvider = ({
  children,
  history = 0,
}: PanelProviderProps) => {
  const [openPanelIds, setOpenPanelIds] = useState<string[]>([]);

  const openPanel = useCallback(
    (id: string) => {
      setOpenPanelIds((prevOpenPanelIds) =>
        [...prevOpenPanelIds, id].slice(-(history + 1)),
      );
    },
    [history],
  );

  const closePanel = useCallback((id: string) => {
    setOpenPanelIds((prevOpenPanelIds) =>
      prevOpenPanelIds.filter((panelId) => panelId !== id),
    );
  }, []);

  const openPanelId = useMemo(
    () =>
      openPanelIds.length > 0 ? openPanelIds[openPanelIds.length - 1] : null,
    [openPanelIds],
  );

  return (
    <PanelContext.Provider value={{ openPanel, closePanel, openPanelId }}>
      {children}
    </PanelContext.Provider>
  );
};

export const usePanelContext = () => {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanelContext must be used within PanelProvider');
  }
  return context;
};

export const useShouldPanelBeVisible = (panelId: string) => {
  const { openPanelId } = usePanelContext();
  return openPanelId === panelId;
};
